import store from './store'
import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {openAlertSystemLayer} from './layers/launchers/AlertSystemLayerHandle'
import {ConfigurationPromo} from '../types/configurationPromo'
import {DealerData} from '../types/dealer'
import {Global, Params} from '../shared/variables'
import isEmpty from 'lodash.isempty'
import {DEFAULT_ACTIVITY_TYPE, DEFAULT_CONTRACT_TYPE} from '../shared/utilities'
import {
    normalizeConfigurationPromo,
    normalizePromotionList,
} from '../shared/configurationPromoDefaults'

export type AppDispatch = typeof store.dispatch

export const fetchConfigurationPromo = createAsyncThunk(
    'configurationPromo/fetchConfigurationPromo',
    async (_, {signal}) => {
        try {
            const campaignCode = store.getState().general.campaignCode

            const response = await axios.get<ConfigurationPromo>(
                `${store.getState().general.backendUrl}/${Params.PROMOTIONS}/${
                    campaignCode ? `${campaignCode}` : ''
                }`,
                {signal}
            )

            return normalizeConfigurationPromo(response.data)
        } catch (err: any) {
            if (signal.aborted) {
                throw err
            }
            if (err?.response?.status === Global.ERROR_404) {
                return normalizeConfigurationPromo({
                    cta_opening: {
                        hide_cta: true,
                    },
                    coupon: {
                        recoverable: false,
                    },
                })
            }
            openAlertSystemLayer()
            throw err
        }
    }
)

export const fetchAllProvinces = createAsyncThunk(
    'provinces/fetchAllProvinces',
    async (_, {signal}) => {
        try {
            const response = await axios.get<ConfigurationPromo>(
                `${store.getState().general.backendUrl}/${Params.PROVINCES}`,
                {signal}
            )

            return response.data
        } catch (err) {
            if (!signal.aborted) {
                openAlertSystemLayer()
            }
            throw err
        }
    }
)

const fetchDealers = async (
    url: string,
    fallbackCode?: string,
    silent?: boolean,
    signal?: AbortSignal
): Promise<DealerData[]> => {
    try {
        const response = await axios.get<DealerData[]>(url, {signal})

        if (
            Array.isArray(response.data) &&
            response.data.length === 0 &&
            fallbackCode
        ) {
            const fallbackUrl = url.replace(
                `${Params.BEARER}=${fallbackCode}`,
                `${Params.COMPANY_CODE}=${fallbackCode}`
            )
            const fallbackResponse = await axios.get<DealerData[]>(
                fallbackUrl,
                {signal}
            )

            return fallbackResponse.data
        }

        return response.data
    } catch (err) {
        if (!silent && !signal?.aborted) {
            openAlertSystemLayer()
        }
        throw err
    }
}

const buildDealerSearchUrl = (myDealerCode?: string) => {
    const dealerSearchParams =
        store.getState().configurationPromo.configuration.dealers_search_params
    let dynamicFetchUrl

    if (dealerSearchParams) {
        const activityType =
            dealerSearchParams.activity_type || DEFAULT_ACTIVITY_TYPE
        const contractType =
            dealerSearchParams.contract_type || DEFAULT_CONTRACT_TYPE
        const searchBrand = dealerSearchParams.brand || 'VW'

        dynamicFetchUrl = `${store.getState().general.backendUrl}/${
            Params.DEALERS
        }/?${Params.BRAND}=${searchBrand}&${
            Params.ACTIVITY_TYPE
        }=${activityType}&${Params.CONTRACT_TYPE}=${contractType}&${
            Params.ORDER_BY
        }=${Params.CLEANED_COMPANY}`

        if (myDealerCode) {
            dynamicFetchUrl += `&${Params.BEARER}=${myDealerCode}`
        } else {
            if (!isEmpty(dealerSearchParams.blacklisted_codes)) {
                dynamicFetchUrl += `&${Params.NOT_COMPANY_CODES}=${dealerSearchParams.blacklisted_codes}`
            }
        }

        return dynamicFetchUrl
    }

    const brand = store.getState().general.brand
    dynamicFetchUrl = `${store.getState().general.backendUrl}/${
        Params.DEALERS
    }?${Params.BRAND}=${brand}`
    if (myDealerCode) {
        dynamicFetchUrl += `&${Params.ACTIVITY_TYPE}=${DEFAULT_ACTIVITY_TYPE}&${Params.CONTRACT_TYPE}=${DEFAULT_CONTRACT_TYPE}&${Params.BEARER}=${myDealerCode}`
    }

    return dynamicFetchUrl
}

export const fetchAllDealers = createAsyncThunk(
    'dealers/fetchAllDealers',
    async (_, {signal}) => {
        const dynamicFetchUrl = buildDealerSearchUrl()

        return fetchDealers(dynamicFetchUrl, undefined, false, signal)
    }
)

export const fetchMyDealerBranches = createAsyncThunk(
    'dealers/fetchMyDealerBranches',
    async (myDealerCode: string, {signal}) => {
        const dynamicFetchUrl = buildDealerSearchUrl(myDealerCode)

        return fetchDealers(dynamicFetchUrl, myDealerCode, true, signal)
    }
)

export const fetchMyDealerPromotions = createAsyncThunk(
    'dealers/fetchMyDealerPromotions',
    async (myDealerCode: string, {signal}) => {
        const brand = store.getState().general.brand
        const response = await axios.get<ConfigurationPromo>(
            `${store.getState().general.backendUrl}/${brand}/${Params.DEALER}/${
                Params.PROMOTIONS
            }/${myDealerCode}`,
            {signal}
        )

        return normalizePromotionList(response.data)
    }
)

export const fetchAllPromotions = createAsyncThunk(
    'promotions/fetchAllPromotions',
    async (_, {signal}) => {
        const brand = store.getState().general.brand
        try {
            const response = await axios.get<ConfigurationPromo>(
                `${store.getState().general.backendUrl}/${brand}/${
                    Params.PROMOTIONS
                }`,
                {signal}
            )

            return normalizePromotionList(response.data)
        } catch (err) {
            if (!signal.aborted) {
                openAlertSystemLayer()
            }
            throw err
        }
    }
)
